import BlogSliderCard from '@/components/dashboard/index/BlogSliderCard';
import ContactSupportCard from '@/components/dashboard/index/ContactSupportCard';
import { DeletePropertyCard } from '@/components/dashboard/index/DeletePropertyCard';
import NextStepsCard from '@/components/dashboard/index/NextStepsCard';
import { ProTipCard } from '@/components/dashboard/index/ProTipCard';
import { SellersGuideCard } from '@/components/dashboard/index/SellersGuideCard';
import NoPropertiesCard from '@/components/dashboard/index/NoProperties';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDashboardPropertyMaybe } from '@/hooks/useDashboardProperty';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  CreditCard,
  FileText,
  Home,
  Shield,
  Sparkles
} from 'lucide-react';

export const Route = createFileRoute('/_protected/dashboard/')({
  component: PropertyDashboardContent,
});

function PropertyDashboardContent() {
  const { property } = useDashboardPropertyMaybe()

  const showNoPropertiesCard = !property || !(property.street_number && property.street_name)
  if (showNoPropertiesCard) {
    return <NoPropertiesCard />
  }

  return <ModernDashboard />
}

function ModernDashboard() {
  const { property } = useDashboardPropertyMaybe();

  if (!property) {
    return <NoPropertiesCard />
  }

  // Now we can safely use the computed values
  const progress = getProgress();
  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  // Helper functions
  function getProgress() {
    if (!property) return 0;
    const status = property.status || 'draft';
    const steps = ['draft', 'w4_payment', 'w4_id_verification', 'w4_signing', 'active'];
    const currentIndex = steps.indexOf(status);
    return Math.max(0, (currentIndex + 1) / steps.length * 100);
  }

  function getStatusInfo() {
    if (!property) return { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: AlertCircle };
    const status = property.status || 'draft';
    switch (status) {
      case 'draft':
        return { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: AlertCircle };
      case 'w4_payment':
        return { label: 'Payment Pending', color: 'bg-amber-100 text-amber-700', icon: CreditCard };
      case 'w4_id_verification':
        return { label: 'Verification Pending', color: 'bg-blue-100 text-blue-700', icon: Shield };
      case 'w4_signing':
        return { label: 'Documents Pending', color: 'bg-purple-100 text-purple-700', icon: FileText };
      case 'active':
        return { label: 'Live', color: 'bg-green-100 text-green-700', icon: CheckCircle2 };
      default:
        return { label: 'Draft', color: 'bg-slate-100 text-slate-700', icon: AlertCircle };
    }
  }



  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="relative">
        <div className="absolute inset-0 bg-linear-to-r from-blue-600/5 to-indigo-600/5 rounded-2xl" />
        <div className="relative p-8 lg:p-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge className={`${statusInfo.color} border-0 font-medium px-3 py-1`}>
                  <StatusIcon className="h-3 w-3 mr-1.5" />
                  {statusInfo.label}
                </Badge>
                <span className="text-sm text-slate-500">
                  Updated {new Date().toLocaleDateString()}
                </span>
              </div>

              <div className="space-y-2">
                <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
                  {property.street_number} {property.street_name}
                </h1>
                <p className="text-xl text-slate-600">
                  {property.municipality}, {property.state_province}
                </p>
              </div>

              <div className="flex items-center gap-6 text-slate-600">
                {(property.is_freehold ? property.freehold_property_type : property.condo_property_type) && (
                  <div className="flex items-center gap-2">
                    <Home className="h-4 w-4" />
                    <span>{property.is_freehold ? property.freehold_property_type : property.condo_property_type}</span>
                  </div>
                )}
                {(property.total_rooms || 0) > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{property.total_rooms}</span>
                    <span>rooms</span>
                  </div>
                )}
                {property.approx_square_footage && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{property.approx_square_footage}</span>
                    <span>sqft</span>
                  </div>
                )}
              </div>

              <div className="pt-2">
                <div className="text-4xl font-bold text-slate-900">
                  {property.list_price != null
                    ? `$${property.list_price.toLocaleString()}`
                    : 'Price on request'}
                </div>
              </div>
            </div>

            {/* Property Image */}
            <div className="lg:w-96 h-64 lg:h-80 relative rounded-2xl overflow-hidden shadow-xl">
              {property.images?.length > 0 ? (
                <img
                  src={property.images[0].original_url}
                  alt={`${property.street_number} ${property.street_name}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-linear-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                  <Home className="h-16 w-16 text-slate-400" />
                </div>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-8 pt-8 border-t border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">Listing Progress</h3>
              <span className="text-sm font-medium text-slate-600">{Math.round(progress)}% Complete</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div
                className="bg-linear-to-r from-blue-600 to-indigo-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Main Action */}
        <div className="lg:col-span-2 space-y-6">
          <NextStepsCard />

          {/* Quick Actions */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Sparkles className="h-5 w-5 text-blue-600" />
                </div>
                <CardTitle className="text-xl font-semibold text-slate-900">Quick Actions</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button variant="outline" className="h-auto p-4 justify-start group" asChild>
                  <Link to="/dashboard/edit">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-blue-50 rounded-full flex items-center justify-center">
                        <Home className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="text-left flex-1">
                        <div className="font-medium text-slate-900">Edit Property</div>
                        <div className="text-xs text-slate-500">Update details & photos</div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </Link>
                </Button>

                <Button variant="outline" className="h-auto p-4 justify-start group" asChild>
                  <Link to="/dashboard/documents">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 bg-green-50 rounded-full flex items-center justify-center">
                        <FileText className="h-4 w-4 text-green-600" />
                      </div>
                      <div className="text-left flex-1">
                        <div className="font-medium text-slate-900">View Documents</div>
                        <div className="text-xs text-slate-500">Contracts & agreements</div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          <BlogSliderCard />
        </div>

        {/* Right Column - Support & Resources */}
        <div className="space-y-6">
          <ContactSupportCard />
          <SellersGuideCard />

          {/* Tips Card */}
          <ProTipCard />

          <DeletePropertyCard property={property} />
        </div>
      </div>
    </div>
  );
}
